<script lang="ts">
	import { createForm } from 'svelte-forms-lib';
	import * as yup from 'yup';

	import Textfield from '@smui/textfield/styled';
	import SmuiButton, { Label as SmuiLabel, Icon as ButtonIcon } from '@smui/button/styled';
	import { getClient, mutate, query } from 'svelte-apollo';
	import Dialog, { Title, Content, Actions } from '@smui/dialog/styled';
	import { Link } from 'svelte-routing';
	import { APP_PATHS } from '../../../constants';
	import { graphQLErrors as graphQLErrorsStore, userInfo, userClientData } from '../../../stores';
	import AppContainer from '../../AppContainer.svelte';
	import Text from '../../Text.svelte';
	import Card from '../../Card.svelte';
	import CardRow from '../../CardRow.svelte';
	import Button from '../../Button.svelte';
	import { copySegment, getLastSegmentQuery } from '../../../graphql/queries/segments';
	import { onMount } from 'svelte';
	import type { ApolloQueryResult } from 'apollo-boost';

	export let id: string;

	let isSaved: boolean;
	let errorMessage: string;
	let errorDialog = false;
	let okDialog = false;
	let lastSegmentId = 0;
	const client = getClient();

	const getLastSegmentOp = query(client, {
		query: getLastSegmentQuery
	});

	const createSegmentOp = (values: any) => {
		const { personaName } = values;

		return new Promise((resolve) =>
			mutate(client, {
				mutation: copySegment,
				variables: {
					id: id,
					name: $userClientData.webData.nameFunc(
						personaName,
						$userInfo.client.namePostfix,
						'',
						'',
						[],
						[],
						[],
						[],
						[],
						[],
						lastSegmentId,
						false,
						false
					)
				},
				errorPolicy: 'all'
			})
				.then((dataResponse) => {
					resolve(dataResponse);
				})
				.catch((err) => {
					console.warn(err);
					resolve({ errors: err });
				})
		);
	};

	const { form, errors, isValid, handleChange, handleSubmit } = createForm({
		initialValues: {
			personaName: ''
		},
		validationSchema: yup.object().shape({
			personaName: yup.string().required().min(3).max(128)
		}),
		onSubmit: async (values) => {
			await getLastSegmentOp.refetch();
			getLastSegmentOp.subscribe(async (promise) => {
				await (promise as Promise<ApolloQueryResult<any>>).then((data) => {
					lastSegmentId = data.data.lsegment[0].segmentIntId;
				});
			});
			graphQLErrorsStore.set(undefined);
			return createSegmentOp(values)
				.then(() => {
					isSaved = true;
					okDialog = true;
				})
				.catch((err) => {
					handleError(err.message);
				});
		}
	});

	function handleError(errMsg: string) {
		errorMessage = errMsg;
		errorDialog = true;
		console.error('Error deleting segment', errMsg);
	}

	onMount(async () => {
		await getLastSegmentOp.refetch();
		getLastSegmentOp.subscribe(async (promise) => {
			await (promise as Promise<ApolloQueryResult<any>>).then((data) => {
				lastSegmentId = data.data.lsegment[0].segmentIntId;
			});
		});
	});
</script>

<AppContainer>
	<form class:valid={$isValid} on:submit={handleSubmit}>
		<Text gutter type="headline2">Duplicate persona</Text>
		<Text gutter type="body1"
			>Please enter new name for the copy of persona you want to duplicate</Text
		>
		<div class="margin" />
		<Card noborder>
			<div class="row">
				<Text>Name:</Text>
				<div class="fullwidth">
					<Textfield
						invalid={$errors['personaName']}
						input$name="personaName"
						class="fullwidth"
						variant="outlined"
						value={$form.personaName}
						on:change={handleChange}
						on:blur={handleChange}
					/>
				</div>
				<div class="text-end">
					<Text>
						{$userClientData.webData.nameEnd}
					</Text>
				</div>
			</div>
		</Card>
		<CardRow>
			<Card noborder inline>
				<div class="row confirmation">
					<div>
						<SmuiButton type="submit" variant="raised" disabled={isSaved}>
							<ButtonIcon class="material-icons">save</ButtonIcon>
							<SmuiLabel>Save</SmuiLabel>
						</SmuiButton>
					</div>
				</div>
			</Card>
		</CardRow>
	</form>
</AppContainer>

<Dialog
	open={okDialog}
	aria-labelledby="dialog-title"
	aria-describedby="dialog-content"
	scrimClickAction=""
	escapeKeyAction=""
>
	<Title id="dialog-title">Copy of segment {id} was created</Title>
	<Content id="dialog-content">
		You can view it in
		<Link to={APP_PATHS.DASHBOARD}>Dashboard</Link>
	</Content>

	<Actions>
		<Button on:click={() => (okDialog = false)}>Close</Button>
	</Actions>
</Dialog>

<Dialog
	open={errorDialog}
	aria-labelledby="dialog-title"
	aria-describedby="dialog-content"
	scrimClickAction=""
	escapeKeyAction=""
>
	<Title id="dialog-title">Error during duplicating the segment</Title>
	<Content id="dialog-content">{errorMessage}</Content>

	<Actions>
		<Button on:click={() => (errorDialog = false)}>Close</Button>
	</Actions>
</Dialog>

<style>
	:global(*) {
		box-sizing: border-box;
		margin: 0;
	}
	.margin {
		margin-top: 50px;
	}
	.row {
		display: flex;
		text-align: left;
		padding: 14px 0;
		flex-direction: column;
	}

	.row > :global(div:first-child) {
		padding-bottom: 12px;
	}

	.confirmation {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		padding: 50px 0 30px;
	}

	.confirmation div:first-child {
		padding-bottom: 0;
	}

	.confirmation :global(div:first-child button) {
		font-size: 20px;
		height: 56px;
		padding: 0 24px;
	}

	.confirmation :global(div:first-child .mdc-button__icon) {
		font-size: 20px;
		height: 20px;
		margin-right: 18px;
	}

	@media screen and (min-width: 600px) {
		.row {
			flex-direction: row;
			align-items: center;
		}
		.row > :global(div:first-child) {
			min-width: 110px;
			padding-bottom: 0;
		}
	}

	.text-end {
		margin-left: 20px;
		margin-right: 20px;
	}
</style>
